// Artwork Details (T16) display helpers for the vendor-facing PO spec —
// shared between the public PO document (PublicPOPage /po/:uuid) and the
// staff PO detail preview (PurchaseOrderDetailPage) so the two renderings
// of the same spec can't drift.

/** The structured artwork-spec fields a decoration row carries (T16), plus
 *  whether an artwork image is attached — the minimal shape the helpers need.
 *  Both pages' richer PoDecoration interfaces satisfy this structurally. */
export interface ArtworkDetailsFields {
  logoName: string | null;
  logoColor: string | null;
  proofType: string | null;
  logoWidthIn: string | null;
  logoHeightIn: string | null;
  useMaxSize: boolean;
  /** A typed imprint size (Stephanie 2026-11-04). Optional: older payloads. */
  logoSize?: string | null;
  repeatLogo: string | null;
  artwork: unknown;
}

// Vendor-facing proof-type labels (map §Stage 4/7).
const PROOF_TYPE_LABELS: Record<string, string> = {
  // Same words as the quote's dropdown (Stephanie 2026-10-14).
  none: "No proof required",
  email_proof: "E-mail digital proof",
  digital_photo: "Digital photo",
  pre_production: "E-mail pre-production photo",
};

export function proofTypeLabel(proofType: string | null | undefined): string {
  if (!proofType) return "";
  return PROOF_TYPE_LABELS[proofType] ?? proofType.replace(/_/g, " ");
}

/** Format the imprint size line: "Use max size", explicit "W × H in", or both.
 *  Empty string when there's nothing to show (drives block omission). */
export function imprintSizeLabel(d: ArtworkDetailsFields): string {
  const trim = (s: string | null): string => (s ? s.trim() : "");
  const w = trim(d.logoWidthIn);
  const h = trim(d.logoHeightIn);
  const dims = w && h ? `${w} × ${h} in` : w ? `${w} in` : h ? `${h} in` : "";
  const typed = trim(d.logoSize ?? null);
  if (d.useMaxSize) return [dims ? `Use max size (${dims})` : "Use max size", typed].filter(Boolean).join(" · ");
  return [dims, typed].filter(Boolean).join(" · ");
}

/** True when a decoration carries ANY structured artwork spec OR an artwork
 *  image — the Artwork Details block is omitted entirely when this is false
 *  (no empty shell), and the image lives inside it (R2). */
export function hasArtworkDetails(d: ArtworkDetailsFields): boolean {
  return (
    !!(d.logoName && d.logoName.trim()) ||
    !!(d.logoColor && d.logoColor.trim()) ||
    !!d.proofType ||
    !!(d.repeatLogo && d.repeatLogo.trim()) ||
    imprintSizeLabel(d) !== "" ||
    d.artwork != null
  );
}
